import { rivermeadowApi } from '../api';

export const moveGroupsAndAppsApi = rivermeadowApi.injectEndpoints({
    endpoints: (builder) => ({
        // Get all move groups for a project/organization
        getDiscoveryMoveGroups: builder.query({
            query: (organizationId) => ({
                url: `organizations/${organizationId}/discovery-move-groups`,
                method: 'GET',
            }),
            providesTags: ['MOVE_GROUPS'],
        }),

        // Get a specific move group by ID
        getMoveGroupById: builder.query({
            query: ({ moveGroupId, size = 10, page = 0 }) => ({
                url: `discovery/move-groups/${moveGroupId}`,
                method: 'GET',
                params: { size, page },
            }),
            providesTags: (result, error, moveGroupId) => [{ type: 'MOVE_GROUPS', id: moveGroupId }],
        }),

        // Create a new move group
        createMoveGroup: builder.mutation({
            query: ({ organizationId, name, externalId, user, date }) => ({
                url: `discovery/move-groups`,
                method: 'POST',
                body: {
                    organization_id: organizationId,
                    name,
                    ...(externalId && { external_id: externalId }),
                    ...(user !== undefined && { assigned_user_id: user }),
                    ...(date !== undefined && { target_migration_date: date }),
                },
            }),
            invalidatesTags: ['MOVE_GROUPS', 'APPLICATIONS'],
        }),

        // Update an existing move group
        updateMoveGroup: builder.mutation({
            query: ({ moveGroupId, name, user, date }) => ({
                url: `discovery/move-groups/${moveGroupId}`,
                method: 'PUT',
                body: {
                    name,
                    ...(user !== undefined && { assigned_user_id: user }),
                    ...(date !== undefined && { target_migration_date: date }),
                },
            }),
            invalidatesTags: [
                'MOVE_GROUPS',
                'APPLICATIONS',
                'DISCOVERY_INVENTORY',
                'FILTERED_DISCOVERY_INVENTORY',
                'UNSORTED_INVENTORY',
            ],
        }),

        // Delete a move group
        deleteMoveGroup: builder.mutation({
            query: (moveGroupId) => ({
                url: `discovery/move-groups/${moveGroupId}`,
                method: 'DELETE',
            }),
            invalidatesTags: [
                'MOVE_GROUPS',
                'APPLICATIONS',
                'DISCOVERY_INVENTORY',
                'FILTERED_DISCOVERY_INVENTORY',
                'UNSORTED_INVENTORY',
            ],
        }),

        // Get all applications for a project/organization
        getApplications: builder.query({
            query: (organizationId) => ({
                url: `organizations/${organizationId}/discovery-applications`,
                method: 'GET',
            }),
            providesTags: ['APPLICATIONS'],
        }),

        // Get a specific application by ID
        getApplicationById: builder.query({
            query: ({ applicationId, size = 10, page = 0 }) => ({
                url: `discovery/applications/${applicationId}`,
                params: { size, page },
                method: 'GET',
            }),
            providesTags: (result, error, applicationId) => [{ type: 'APPLICATIONS', id: applicationId }],
        }),

        // Create a new application
        createApplication: builder.mutation({
            query: ({ organizationId, name, moveGroupIds, externalId }) => ({
                url: `discovery/applications`,
                method: 'POST',
                body: {
                    organization_id: organizationId,
                    name,
                    ...(moveGroupIds && moveGroupIds.length > 0 && { move_group_ids: moveGroupIds }),
                    ...(externalId && { external_id: externalId }),
                },
            }),
            invalidatesTags: ['APPLICATIONS', 'MOVE_GROUPS'],
        }),

        // Update an existing application
        updateApplication: builder.mutation({
            query: ({ applicationId, name, moveGroupIds }) => ({
                url: `discovery/applications/${applicationId}`,
                method: 'PUT',
                body: {
                    name,
                    ...(moveGroupIds !== undefined && { move_group_ids: moveGroupIds }),
                },
            }),
            invalidatesTags: [
                'APPLICATIONS',
                'MOVE_GROUPS',
                'DISCOVERY_INVENTORY',
                'FILTERED_DISCOVERY_INVENTORY',
                'UNSORTED_INVENTORY',
            ],
        }),

        // Delete an application
        deleteApplication: builder.mutation({
            query: (applicationId) => ({
                url: `discovery/applications/${applicationId}`,
                method: 'DELETE',
            }),
            invalidatesTags: [
                'APPLICATIONS',
                'MOVE_GROUPS',
                'DISCOVERY_INVENTORY',
                'FILTERED_DISCOVERY_INVENTORY',
                'UNSORTED_INVENTORY',
            ],
        }),

        // Add VMs to a move group
        addVMsToMoveGroup: builder.mutation({
            query: ({ vmIds, moveGroupId }) => ({
                url: 'discovery/normalized-vm-infos/move-group',
                method: 'POST',
                body: {
                    ids: vmIds,
                    move_group_id: moveGroupId,
                },
            }),
            invalidatesTags: [
                'MOVE_GROUPS',
                'APPLICATIONS',
                'DISCOVERY_INVENTORY',
                'FILTERED_DISCOVERY_INVENTORY',
                'UNSORTED_INVENTORY',
            ],
        }),

        // Add VMs to an application
        addVMsToApplication: builder.mutation({
            query: ({ vmIds, applicationId, moveGroupId }) => ({
                url: 'discovery/normalized-vm-infos/application',
                method: 'POST',
                body: {
                    ids: vmIds,
                    application_id: applicationId,
                    ...(moveGroupId !== undefined && { move_group_id: moveGroupId }),
                },
            }),
            invalidatesTags: [
                'APPLICATIONS',
                'MOVE_GROUPS',
                'DISCOVERY_INVENTORY',
                'FILTERED_DISCOVERY_INVENTORY',
                'UNSORTED_INVENTORY',
            ],
        }),

        // Assign/unassign VMs to Move Groups and Applications (unified endpoint)
        assignVMsToMoveGroupOrApplication: builder.mutation({
            query: ({ vmIds, moveGroupId, applicationId }) => ({
                url: 'discovery/normalized-vm-infos/move-group-application-assignment',
                method: 'POST',
                body: {
                    ids: vmIds,
                    ...(moveGroupId !== undefined && { move_group_id: moveGroupId }),
                    ...(applicationId !== undefined && { application_id: applicationId }),
                },
            }),
            invalidatesTags: [
                'MOVE_GROUPS',
                'APPLICATIONS',
                'DISCOVERY_INVENTORY',
                'FILTERED_DISCOVERY_INVENTORY',
                'UNSORTED_INVENTORY',
            ],
        }),

        // Remove VMs from move group (unassign)
        removeVMsFromMoveGroup: builder.mutation({
            query: ({ vmIds }) => ({
                url: 'discovery/normalized-vm-infos/move-group',
                method: 'POST',
                body: {
                    ids: vmIds,
                    move_group_id: null,
                },
            }),
            invalidatesTags: [
                'MOVE_GROUPS',
                'DISCOVERY_INVENTORY',
                'FILTERED_DISCOVERY_INVENTORY',
                'UNSORTED_INVENTORY',
            ],
        }),

        // Remove VMs from application (unassign)
        removeVMsFromApplication: builder.mutation({
            query: ({ vmIds }) => ({
                url: 'discovery/normalized-vm-infos/application',
                method: 'POST',
                body: {
                    ids: vmIds,
                    application_id: null,
                },
            }),
            invalidatesTags: [
                'APPLICATIONS',
                'DISCOVERY_INVENTORY',
                'FILTERED_DISCOVERY_INVENTORY',
                'UNSORTED_INVENTORY',
            ],
        }),

        // Unassign all VMs and applications from a move group (clear all assignments)
        unassignMoveGroupAssignments: builder.mutation({
            query: ({ moveGroupId }) => ({
                url: `discovery/move-groups/${moveGroupId}/assignments`,
                method: 'PATCH',
                body: {
                    unassign_applications: true,
                    unassign_vms: true,
                },
            }),
            invalidatesTags: [
                'MOVE_GROUPS',
                'APPLICATIONS',
                'DISCOVERY_INVENTORY',
                'FILTERED_DISCOVERY_INVENTORY',
                'UNSORTED_INVENTORY',
            ],
        }),
    }),
});

export const {
    // Move Group Queries
    useGetDiscoveryMoveGroupsQuery,
    useLazyGetDiscoveryMoveGroupsQuery,
    useGetMoveGroupByIdQuery,
    useLazyGetMoveGroupByIdQuery,

    // Application Queries
    useGetApplicationsQuery,
    useLazyGetApplicationsQuery,
    useGetApplicationByIdQuery,
    useLazyGetApplicationByIdQuery,

    // Mutations
    useCreateMoveGroupMutation,
    useUpdateMoveGroupMutation,
    useDeleteMoveGroupMutation,
    useCreateApplicationMutation,
    useUpdateApplicationMutation,
    useDeleteApplicationMutation,
    useAddVMsToMoveGroupMutation,
    useAddVMsToApplicationMutation,
    useAssignVMsToMoveGroupOrApplicationMutation,
    useRemoveVMsFromMoveGroupMutation,
    useRemoveVMsFromApplicationMutation,
    useUnassignMoveGroupAssignmentsMutation,
} = moveGroupsAndAppsApi;
